import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { Trash2, Plus, X, Copy, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const UNASSIGNED = "__unassigned__";

function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function shiftHours(s) {
  try {
    const [h1, m1] = s.start_time.split(":").map(Number);
    const [h2, m2] = s.end_time.split(":").map(Number);
    const start = h1 * 60 + m1;
    let end = h2 * 60 + m2;
    if (end <= start) end += 24 * 60; // crosses midnight
    let mins = end - start - (Number(s.break_minutes) || 0);
    if (mins < 0) mins = 0;
    return mins / 60;
  } catch {
    return 0;
  }
}

function DraggableShift({ id, shift, editable, onClick, onRemove, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled: !editable,
  });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 40 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-2 rounded-md border text-xs mb-1 select-none bg-[color:var(--surface-2)] border-[color:var(--gold)]/40 hover:border-[color:var(--gold)] transition-colors ${editable ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}
      data-testid={`grid-${id}`}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center justify-between gap-1">
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
          className="font-semibold text-[color:var(--gold)] text-left flex-1 truncate"
          data-testid={`grid-${id}-open`}
        >
          {shift.start_time}–{shift.end_time}
        </button>
        {editable && (
          <span className="flex items-center gap-1">
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              className="text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]"
              title="Duplicate"
              data-testid={`grid-${id}-dup`}
            >
              <Copy size={12} />
            </button>
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              className="text-[color:var(--error)]"
              title="Remove"
              data-testid={`grid-${id}-rm`}
            >
              <Trash2 size={12} />
            </button>
          </span>
        )}
      </div>
      <div className="text-[color:var(--text-secondary)] mt-0.5 truncate">
        {shift.role || shift.department}
        <span className="ml-1 opacity-60">· {shiftHours(shift).toFixed(1)}h</span>
      </div>
      {shift.notes && <div className="text-[10px] opacity-70 truncate">{shift.notes}</div>}
    </div>
  );
}

function DroppableCell({ id, editable, templates, onAddBlank, onAddTemplate, children }) {
  const { isOver, setNodeRef } = useDroppable({ id, disabled: !editable });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const displayEnd = (t) => (t === "00:00" ? "24:00" : t);

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[80px] border rounded-md p-1 transition-colors relative ${
        isOver
          ? "bg-[color:var(--gold)]/15 border-[color:var(--gold)]"
          : "border-[color:var(--border)]"
      }`}
      data-testid={id}
    >
      {children}
      {editable && (
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="w-full mt-1 text-[11px] text-[color:var(--text-secondary)] hover:text-[color:var(--gold)] flex items-center justify-center gap-1 py-1 rounded-sm hover:bg-[color:var(--surface-2)]"
            data-testid={`${id}-add`}
          >
            <Plus size={11} /> add <ChevronDown size={10} />
          </button>
          {menuOpen && (
            <div
              className="absolute z-30 top-full left-0 right-0 mt-1 rounded-md border border-[color:var(--border)] bg-[color:var(--bg-surface-2)] shadow-lg overflow-hidden"
              data-testid={`${id}-add-menu`}
            >
              {templates && templates.length > 0 && (
                <div className="max-h-[220px] overflow-y-auto">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-[color:var(--gold)]/10"
                      onClick={() => { setMenuOpen(false); onAddTemplate(t); }}
                      data-testid={`${id}-add-tpl-${t.id}`}
                    >
                      <div className="font-semibold text-[color:var(--gold)] truncate">{t.name}</div>
                      <div className="text-[color:var(--text-secondary)]">
                        {t.start_time}–{displayEnd(t.end_time)} · {(t.hours || 0).toFixed(1)}h
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="w-full text-left px-2 py-1.5 text-[11px] border-t border-[color:var(--border)] hover:bg-[color:var(--surface-2)] text-[color:var(--text-secondary)]"
                onClick={() => { setMenuOpen(false); onAddBlank(); }}
                data-testid={`${id}-add-blank`}
              >
                Blank shift (10:00–18:00)
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ScheduleGrid({
  shifts,
  employees,
  weekStart,
  editable,
  department,
  onChange,
}) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [templates, setTemplates] = useState([]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  useEffect(() => {
    api.get("/shift_templates").then(({ data }) => {
      setTemplates((data || []).filter((t) => t.active !== false));
    }).catch(() => setTemplates([]));
  }, []);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const rows = useMemo(() => {
    const ids = [];
    const seen = new Set();
    const push = (id) => { if (!seen.has(id)) { seen.add(id); ids.push(id); } };
    push(UNASSIGNED);
    employees.forEach((e) => push(e.id));
    shifts.forEach((s) => { if (s.employee_id) push(s.employee_id); });
    return ids.map((eid) => {
      if (eid === UNASSIGNED) return { id: UNASSIGNED, name: "Unassigned", weekly: 0 };
      const emp = employees.find((e) => e.id === eid);
      return { id: eid, name: emp?.name || "Unknown", weekly: 0 };
    });
  }, [employees, shifts]);

  const weeklyByEmp = useMemo(() => {
    const m = {};
    shifts.forEach((s) => {
      const k = s.employee_id || UNASSIGNED;
      m[k] = (m[k] || 0) + shiftHours(s);
    });
    return m;
  }, [shifts]);

  const cellShifts = (empId, date) =>
    shifts
      .map((s, idx) => ({ s, idx }))
      .filter(({ s }) => (s.employee_id || UNASSIGNED) === empId && s.date === date);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || !editable) return;
    const idx = Number(String(active.id).replace("shift-", ""));
    if (Number.isNaN(idx)) return;
    const parts = String(over.id).split("::");
    if (parts[0] !== "cell") return;
    const empId = parts[1];
    const date = parts[2];
    if (!empId || !date) return;
    const target = shifts[idx];
    if (!target) return;
    const employee_id = empId === UNASSIGNED ? "" : empId;
    const emp = employees.find((e) => e.id === employee_id);
    const next = shifts.map((s, i) =>
      i === idx
        ? { ...s, employee_id, employee_name: emp?.name || "", date }
        : s
    );
    onChange(next);
  };

  const removeAt = (idx) => onChange(shifts.filter((_, i) => i !== idx));
  const duplicateAt = (idx) => onChange([...shifts, { ...shifts[idx] }]);
  const addAt = (empId, date, template = null) => {
    const emp = empId === UNASSIGNED ? null : employees.find((e) => e.id === empId);
    const base = template || {
      start_time: "10:00", end_time: "18:00", break_minutes: 30,
      role: "Floor", department: department === "All" ? "Floor" : department,
      shift_type: "mid", notes: "",
    };
    onChange([
      ...shifts,
      {
        employee_id: empId === UNASSIGNED ? "" : empId,
        employee_name: emp?.name || "",
        date,
        start_time: base.start_time,
        end_time: base.end_time,
        break_minutes: Number(base.break_minutes) || 0,
        role: base.role || (department === "All" ? "Floor" : department),
        department: base.department || (department === "All" ? "Floor" : department),
        shift_type: base.shift_type || "mid",
        notes: base.notes || "",
      },
    ]);
  };
  const updateAt = (idx, patch) =>
    onChange(shifts.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <div className="overflow-x-auto" data-testid="schedule-grid">
        <div className="min-w-[900px]">
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: "180px repeat(7, minmax(120px, 1fr))" }}
          >
            <div className="label-caps p-2 sticky left-0 bg-[color:var(--surface)]">Employee</div>
            {days.map((d, i) => (
              <div key={d} className="label-caps p-2 text-center">
                {DAY_LABELS[i]}
                <div className="text-[11px] text-[color:var(--text-secondary)] mt-0.5">
                  {d.slice(5)}
                </div>
              </div>
            ))}
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <div className="p-2 border-t border-[color:var(--border)] sticky left-0 bg-[color:var(--surface)]">
                  <div className="text-sm font-semibold truncate">{row.name}</div>
                  <div className="text-[11px] text-[color:var(--text-secondary)]">
                    {(weeklyByEmp[row.id] || 0).toFixed(1)}h / week
                  </div>
                </div>
                {days.map((d) => {
                  const cellId = `cell::${row.id}::${d}`;
                  const items = cellShifts(row.id, d);
                  return (
                    <DroppableCell
                      key={cellId}
                      id={cellId}
                      editable={editable}
                      templates={templates}
                      onAddBlank={() => addAt(row.id, d)}
                      onAddTemplate={(tpl) => addAt(row.id, d, tpl)}
                    >
                      {items.map(({ s, idx }) => (
                        <DraggableShift
                          key={idx}
                          id={`shift-${idx}`}
                          shift={s}
                          editable={editable}
                          onClick={() => setEditingIdx(idx)}
                          onRemove={() => removeAt(idx)}
                          onDuplicate={() => duplicateAt(idx)}
                        />
                      ))}
                    </DroppableCell>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {editingIdx !== null && shifts[editingIdx] && (
        <ShiftEditModal
          shift={shifts[editingIdx]}
          employees={employees}
          editable={editable}
          onClose={() => setEditingIdx(null)}
          onSave={(patch) => {
            const emp = employees.find((e) => e.id === patch.employee_id);
            updateAt(editingIdx, { ...patch, employee_name: emp?.name || "" });
            setEditingIdx(null);
          }}
        />
      )}
    </DndContext>
  );
}

function ShiftEditModal({ shift, employees, editable, onClose, onSave }) {
  const [form, setForm] = useState(shift);
  const set = (patch) => setForm({ ...form, ...patch });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
      onClick={onClose}
      data-testid="edit-shift-modal"
    >
      <div className="hos-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl">Edit Shift</h3>
          <button onClick={onClose} data-testid="edit-shift-close">
            <X size={18} />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="col-span-2">
            <label className="label-caps">Employee</label>
            <select
              className="hos-input"
              value={form.employee_id || ""}
              onChange={(e) => set({ employee_id: e.target.value })}
              disabled={!editable}
              data-testid="edit-shift-employee"
            >
              <option value="">— Unassigned —</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-caps">Date</label>
            <input
              type="date"
              className="hos-input"
              value={form.date}
              onChange={(e) => set({ date: e.target.value })}
              disabled={!editable}
              data-testid="edit-shift-date"
            />
          </div>
          <div>
            <label className="label-caps">Type</label>
            <select
              className="hos-input"
              value={form.shift_type || "mid"}
              onChange={(e) => set({ shift_type: e.target.value })}
              disabled={!editable}
            >
              {["opening", "mid", "closing", "custom"].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-caps">Start</label>
            <input
              type="time"
              className="hos-input"
              value={form.start_time}
              onChange={(e) => set({ start_time: e.target.value })}
              disabled={!editable}
              data-testid="edit-shift-start"
            />
          </div>
          <div>
            <label className="label-caps">End</label>
            <input
              type="time"
              className="hos-input"
              value={form.end_time}
              onChange={(e) => set({ end_time: e.target.value })}
              disabled={!editable}
              data-testid="edit-shift-end"
            />
          </div>
          <div>
            <label className="label-caps">Break (min)</label>
            <input
              type="number"
              className="hos-input"
              value={form.break_minutes}
              onChange={(e) => set({ break_minutes: Number(e.target.value) })}
              disabled={!editable}
            />
          </div>
          <div>
            <label className="label-caps">Role</label>
            <input
              className="hos-input"
              value={form.role || ""}
              onChange={(e) => set({ role: e.target.value })}
              disabled={!editable}
            />
          </div>
          <div className="col-span-2">
            <label className="label-caps">Notes</label>
            <input
              className="hos-input"
              value={form.notes || ""}
              onChange={(e) => set({ notes: e.target.value })}
              disabled={!editable}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button className="hos-btn-outline" onClick={onClose}>
            Cancel
          </button>
          {editable && (
            <button
              className="hos-btn-primary"
              onClick={() => onSave(form)}
              data-testid="edit-shift-save"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
